import { z } from 'zod';
import { DateTime } from 'luxon';
import { tryParseJSON } from '@/utils/tryParseJSON';
import { ServerErrorLogger } from '@/utils/ServerError';
import { IFetchHelper } from '@/interfaces/helper/IFetchHelper';
import { FetchError, RequestInit, Response } from 'node-fetch';
import { RouteError } from '@/utils/RouteError';

const SLOW_RESPONSE_THRESHOLD_SECONDS = 10;
const NEXUDUS_ERROR_SCHEMA = z.object({
  Message: z.string(),
  Status: z.coerce.number(),
});
const DEFAULT_FAILURE_STATUS = 500;

type NexudusErrorResponse = z.infer<typeof NEXUDUS_ERROR_SCHEMA>;

export class NexudusRateLimitRouteError extends RouteError {
  constructor(
    public readonly route: string,
    public readonly tryAgainAfter: number,
  ) {
    super(
      {
        error: `Nexudus API rate limit exceeded for ${route}. Please try again in ${tryAgainAfter} seconds.`,
        name: 'NexudusRateLimitRouteError',
      },
      429,
    );

    Object.setPrototypeOf(this, NexudusRateLimitRouteError.prototype);
  }
}

/**
 * Helps check the responses from Nexudus API for whether they succeeded or failed,
 * and extract relevant information for the error messages.]
 *
 * There are a ridiculous number of ways in which Nexudus requests can fail.
 */
export class NexudusResponseChecker {
  constructor(
    private readonly fetchHelper: IFetchHelper,
    private readonly errorLogger = ServerErrorLogger.forService('NexudusResponseChecker'),
  ) {}

  private wasFailedStatus(response: Response): boolean {
    return !response.ok;
  }

  private wasFailureBody(bodyIfJSON: unknown): NexudusErrorResponse | null {
    const parsedFailedBody = NEXUDUS_ERROR_SCHEMA.safeParse(bodyIfJSON);

    if (!parsedFailedBody.success) {
      return null;
    }
    if (parsedFailedBody.data.Status === 200) {
      return null;
    }
    return parsedFailedBody.data;
  }

  private wasNotValidJSON(bodyIfJSON: unknown): boolean {
    return bodyIfJSON === undefined;
  }

  private wasSlowResponse(startedAt: DateTime): boolean {
    return DateTime.now().diff(startedAt, 'seconds').seconds > SLOW_RESPONSE_THRESHOLD_SECONDS;
  }
  /**
   * Reads the nexudus API response, throwing an error if it indicates a failure.
   *
   * The response body will be consumed, so you can't read it again after calling this.
   * @param response The fetch response from the Nexudus API
   * @param startedAt The time the request was started - needed to detect slow responses
   * @throws RouteError if the response indicates a failure
   * @returns The parsed response body if successful
   */
  async readResponseOrThrow<T>(response: Response, startedAt: DateTime): Promise<T> {
    const bodyString = (await response.text?.()) || '';

    const bodyIfJSON: unknown | undefined = tryParseJSON(bodyString);

    const messageIfJSON = (bodyIfJSON as { Message?: string })?.Message;
    const notValidJSON = this.wasNotValidJSON(bodyIfJSON);
    const failedBody = this.wasFailureBody(bodyIfJSON);
    const failedStatus = this.wasFailedStatus(response);

    const slowResponse = this.wasSlowResponse(startedAt);

    if (slowResponse) {
      this.errorLogger.logger.warn({
        message: `Nexudus response from ${response.url} was slow (${DateTime.now()
          .diff(startedAt, 'seconds')
          .seconds.toFixed(2)} seconds)`,
      });
    }

    if (failedStatus) {
      const status = response.status;
      if (status === 429) {
        // Throw a special error with some metadata here
        return this.errorLogger
          .fromError(
            new NexudusRateLimitRouteError(
              response.url,
              Number.parseInt(response.headers.get('Retry-After') || '0', 10),
            ),
          )
          .logAndThrowOriginalError();
      }

      this.errorLogger.logger.error({
        message: `Nexudus response indicated failure with status ${response.status} ${response.statusText}`,
        body: bodyString,
      });

      if (messageIfJSON) {
        return this.errorLogger
          .fromMessage(`Nexudus response indicated failure with status ${response.status}: ${messageIfJSON}`)
          .withStatusCode(status)
          .logAndThrowRouteError();
      } else {
        return this.errorLogger
          .fromMessage(
            `Nexudus response indicated failure with status ${response.status} ${response.statusText}`,
          )
          .withStatusCode(status)
          .logAndThrowRouteError();
      }
    }

    if (notValidJSON) {
      const status = failedStatus ? response.status : DEFAULT_FAILURE_STATUS;
      this.errorLogger.logger.error({
        message: `Nexudus response was not valid JSON (status: ${response.status} ${response.statusText})`,
        body: bodyString,
      });

      return this.errorLogger
        .fromMessage(`Nexudus response was not valid JSON ${bodyString}`)
        .withStatusCode(status)
        .logAndThrowRouteError();
    }

    if (failedBody) {
      const statusFromBody = failedBody.Status && failedBody.Status >= 400 ? failedBody.Status : null;
      const statusFromResponse = response.status >= 400 ? response.status : null;

      const status = statusFromBody || statusFromResponse || DEFAULT_FAILURE_STATUS;
      this.errorLogger.logger.error({
        message: `Nexudus response indicated failure: ${failedBody.Message} (status: ${status} ${response.statusText}})`,
        body: bodyIfJSON,
      });

      return this.errorLogger
        .fromMessage(`Nexudus response indicated failure: ${failedBody.Message}`)
        .withStatusCode(status)
        .logAndThrowRouteError();
    }

    return bodyIfJSON as T;
  }

  /**
   * Runs the given fetch request to the given URL, handling errors from the response.
   * @param url The URL to request
   * @param requestInit The fetch request init options
   */
  async requestWithErrorHandling<T>(url: string, requestInit?: RequestInit): Promise<T> {
    const startedAt = DateTime.now();
    let response: Response;
    try {
      response = await this.fetchHelper.fetch(url, requestInit);
    } catch (e) {
      if (e instanceof FetchError) {
        this.errorLogger.logger.error({
          message: `Network error when fetching from Nexudus at ${url}: ${e.code} ${e.message}`,
        });
        return this.errorLogger
          .fromMessage(`Network error when fetching from Nexudus at ${url}: ${e.code} ${e.message}`)
          .withStatusCode(500)
          .logAndThrowRouteError();
      }
      throw e;
    }
    return this.readResponseOrThrow<T>(response, startedAt);
  }
}
